import { memo, useCallback, useEffect } from 'react'

import { useFocusHierarchy, useSetFocus } from '@please/lrud'
import { useLocation, useNavigate } from 'react-router-dom'

import { StorageIds } from '@sportworld/js-sdk/src/constants/storage'

import { PurchaseStatus } from '@/components/PPVPopup/PPVPopup'
import { focusIds } from '@/constants/focus'
import { paths } from '@/constants/paths'
import { useOpenPrevEventPopup } from '@/hooks/useOpenPrevEventPopup'
import { useOpenPrevPagePopup } from '@/hooks/useOpenPrevPagePopup'
import { Keys, closeApp } from '@/platform/platform'
import useConfirmationStore from '@/store/useConfirmationStore'
import { usePPVPopupStore } from '@/store/usePPVPopupStore'
import useStore from '@/store/useStore'
import { handlePageViewAnalyticsEvent } from '@/utils/analyticsUtils'
import { getMainHomePath } from '@/utils/route'
import { isWebOSVersionHigherOrEqual, isWebos } from '@/utils/utils'

const getFocusContainer = (items: string[]) => {
  if (items[1] === focusIds.CONTENT || items[1] === focusIds.SIDEBAR) {
    return items[2]
  } else {
    return items[1]
  }
}

const BackNavRaw = () => {
  const setFocus = useSetFocus()
  const focusHierarchy = useFocusHierarchy()
  const navigate = useNavigate()
  const pathname = useLocation().pathname

  const openPrevPagePopup = useOpenPrevPagePopup()
  const openPrevEventPopup = useOpenPrevEventPopup()

  const closeAppWithWebOSPopup = isWebos() && isWebOSVersionHigherOrEqual('6.0') // System popup is displayed on webOS TV 6.0 or higher

  const backPressHandler = useCallback(() => {
    const state = useStore.getState()
    const { popup, onCancel } = useConfirmationStore.getState()
    const { ppvPopup, setPpvPopup, purchaseStatus } =
      usePPVPopupStore.getState()

    if (popup) {
      setFocus(popup.prevFocusId)
      return onCancel()
    }
    if (ppvPopup) {
      if (purchaseStatus === PurchaseStatus.PROCESS) {
        return
      }
      setFocus(ppvPopup.prevFocusId)
      return setPpvPopup(null)
    }

    const {
      authPopup,
      setAuthPopup,
      paymentPopup,
      setPaymentPopup,
      currentEvent,
      mediaVideo,
      setMediaVideo,
      youTubeVideo,
      listPopup,
      setYouTubeVideo,
      setListPopup,
      subMenuType,
      setSubMenuType,
      usercentricsPopup,
      setUsercentricsPopup,
    } = state.common

    const pagePopup = state.popup.pagePopup
    const { isFullscreen, setIsFullscreen } = state.player

    handlePageViewAnalyticsEvent(pathname)

    if (usercentricsPopup) {
      const isCookieBannerPage = pathname.includes(paths.SETTINGS_COOKIE_BANNER)
      const ucUserInteraction = localStorage.getItem(
        StorageIds.ucUserInteraction,
      )

      if (isCookieBannerPage) {
        ucUserInteraction === 'true' && setUsercentricsPopup(false)
        return
      }
    }

    if (authPopup) {
      setFocus(authPopup.prevFocusId)
      return setAuthPopup(null)
    }

    if (paymentPopup) {
      setFocus(paymentPopup.prevFocusId)
      return setPaymentPopup(null)
    }

    if (mediaVideo && mediaVideo.listOptions) {
      const listContextId = mediaVideo.listOptions.listContextId
      setListPopup(mediaVideo.listOptions)
      setMediaVideo(null)
      setListPopup(null)
      if (listContextId) {
        setFocus(listContextId)
      } else {
        setFocus(focusIds.CONTENT)
      }
      return
    }

    if (listPopup) {
      setListPopup(null)
      return !!currentEvent || !!pagePopup || setFocus(focusIds.CONTENT)
    }

    if (isFullscreen) {
      return setIsFullscreen(false)
    }

    const items = focusHierarchy.map((node) => node.focusId)
    const container = getFocusContainer(items)
    const isBackButton = items[2]?.includes('icon-button')

    const isEventDetailPopupContainer =
      container ===
      `${focusIds.EVENT_PAGE}_${focusIds.POPUP}${focusIds.CONTAINER_MOD}`
    const isPagePopupContainer =
      container ===
      `${focusIds.PAGE}_${focusIds.POPUP}${focusIds.CONTAINER_MOD}`

    if (isBackButton) {
      if (isEventDetailPopupContainer) {
        return openPrevEventPopup()
      }
      if (isPagePopupContainer) {
        return openPrevPagePopup()
      }
      return navigate(-1)
    }

    switch (container) {
      case `${focusIds.USERCENTRICS}_${focusIds.POPUP}`:
        closeAppWithWebOSPopup ? closeApp() : navigate(paths.EXIT)
        break
      case focusIds.MAIN_MENU: {
        const homeOrSpecialHomeRegex = new RegExp(
          `${paths.HOME}|${paths.SPECIAL_HOME}|${paths.CLASSIC_HOME}`,
        )
        const isHomeOrSpecialHomePage = homeOrSpecialHomeRegex.test(pathname)

        if (isHomeOrSpecialHomePage) {
          closeAppWithWebOSPopup ? closeApp() : navigate(paths.EXIT)
        } else {
          navigate(getMainHomePath())
        }
        break
      }
      case focusIds.SETTINGS_MENU:
      case focusIds.EXIT + focusIds.CONTAINER_MOD:
        navigate(getMainHomePath())
        break
      case `${focusIds.EVENT_PAGE}${focusIds.CONTENT}`:
        openPrevEventPopup()
        break
      case `${focusIds.EVENT_PAGE}_${focusIds.POPUP}${focusIds.CONTAINER_MOD}`:
        openPrevEventPopup()
        break
      case `${focusIds.PAGE}${focusIds.CONTENT}`:
        navigate(getMainHomePath())
        break
      case `${focusIds.PAGE}_${focusIds.POPUP}${focusIds.CONTAINER_MOD}`:
        openPrevPagePopup()
        break
      case focusIds.FULLSCREEN_PLAYER:
        if (mediaVideo) {
          setMediaVideo(null)
          break
        }

        if (youTubeVideo) {
          setYouTubeVideo(null)
          break
        }
        navigate(-1)
        break
      case focusIds.FEATURE_SCREEN:
        closeAppWithWebOSPopup ? closeApp() : navigate(paths.EXIT)
        break
      case focusIds.SUB_MENU:
        setSubMenuType(null)
        subMenuType && setTimeout(() => setFocus(subMenuType), 0)
        break
      default:
        try {
          subMenuType && setFocus(focusIds.SUB_MENU)
          setFocus(focusIds.SIDEBAR)
        } catch (error) {
          navigate(getMainHomePath())
        }
        break
    }
  }, [focusHierarchy, openPrevPagePopup, openPrevEventPopup, pathname])

  const keyPressHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode == Keys.BACKSPACE || e.keyCode == Keys.BACK) {
        e.preventDefault()
        backPressHandler()
      }
    },
    [backPressHandler],
  )

  const onKeyPress = useCallback(
    (e: KeyboardEvent) => {
      keyPressHandler(e)
    },
    [keyPressHandler],
  )

  useEffect(() => {
    window.addEventListener('keyup', onKeyPress)
    return () => {
      window.removeEventListener('keyup', onKeyPress)
    }
  }, [onKeyPress])

  return null
}

export const BackNav = memo(BackNavRaw)
